import { reactive, readonly, inject, getCurrentInstance, type InjectionKey, type App } from 'vue';
import { hasAdminRole, type UserAccount, type AuthState, type LoginResult, type LoginParams } from '@/types/auth';
import { login as mockLogin, logout as mockLogout } from '@/mock/auth';
import { getUserStatusCheck } from '@/mock/user';
import { authService } from '@/services/authService';
import { organizationApi } from '@/services/organizationApi';
import {
  getAccessToken,
  getUserJson,
  setUserJson,
  setTokens,
  clearTokens,
  isTokenExpired,
  ACCESS_TOKEN_KEY,
  AUTH_USER_KEY,
} from '@/utils/tokenManager';

/** 是否使用后端 API（基于 VITE_USE_MOCK：不启用 Mock 时调用后端，默认走真实 API） */
const USE_BACKEND_API = import.meta.env.VITE_USE_MOCK !== 'true';

/**
 * 认证状态单例，惰性初始化。
 * 不在模块顶层直接调用 `reactive()`：生产构建下 useAuth 与入口 chunk（含 Vue
 * 运行时）存在循环依赖，若在模块求值阶段调用 reactive，会命中入口 chunk 中尚未
 * 初始化的内部绑定（浏览器报 `isObject is not a function`）。改为首次访问时创建，
 * 届时全部 chunk 已加载完毕。顶层的 loadFromStorage() 一并挪入，会话恢复改由
 * provideAuth()（main.ts 启动时调用）触发，时机与效果等价。
 */
let state: AuthState;
let stateReady = false;

function getState(): AuthState {
  if (!stateReady) {
    stateReady = true;
    state = reactive<AuthState>({
      currentUser: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
    loadFromStorage();
  }
  return state;
}

function loadFromStorage() {
  const token = getAccessToken();
  const userRaw = getUserJson();
  if (token && userRaw) {
    // token 已过期 → 清理会话，不显示"已登录"
    if (isTokenExpired()) {
      console.warn('[Auth] Token expired - clearing stale session');
      clearTokens();
      state.isAuthenticated = false;
      state.currentUser = null;
      state.token = null;
      return;
    }
    try {
      const user = JSON.parse(userRaw) as UserAccount;
      state.token = token;
      state.currentUser = {
        ...user,
        createdAt: new Date(user.createdAt),
        lastLoginAt: user.lastLoginAt ? new Date(user.lastLoginAt) : undefined,
      };
      state.isAuthenticated = true;

      // 异步从服务端同步最新用户数据（不阻塞初始化）
      if (USE_BACKEND_API) {
        syncUserFromServer();
      }
    } catch {
      clearTokens();
    }
  }
}

function saveToStorage() {
  if (state.token && state.currentUser) {
    setUserJson(JSON.stringify(state.currentUser));
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
  }
}

async function syncUserFromServer() {
  try {
    const freshUser = await authService.getCurrentUser();
    if (freshUser) {
      state.currentUser = freshUser;
      saveToStorage();
    }
  } catch {
    // 静默失败，不影响当前会话
  }
}

export interface UseAuthReturn {
  state: Readonly<AuthState>;
  login: (params: LoginParams) => Promise<LoginResult>;
  logout: () => Promise<void>;
  checkAuth: () => boolean;
  updateCurrentUser: (user: Partial<UserAccount>) => void;
  isPersonalSpace: () => boolean;
  refreshUser: () => Promise<void>;
}

const authKey: InjectionKey<UseAuthReturn> = Symbol('auth');

/** 防止 auth:session-expired 事件监听器被重复注册 */
let sessionExpiredListenerRegistered = false;

export function createAuth(): UseAuthReturn {
  // 首次调用时惰性创建 reactive state 并恢复本地会话（此时全部 chunk 已就绪）
  getState();

  async function checkOrgStatus(): Promise<boolean> {
    const user = state.currentUser;
    if (!user || hasAdminRole(user.role)) return true;

    if (USE_BACKEND_API) {
      // 后端模式:登录接口本身已拦截禁用/注销账号,此处从服务端拉取最新状态兜底
      // (禁用发生在登录之后的场景,如下发封禁时用户已有会话)
      try {
        const fresh = await authService.getCurrentUser();
        if (fresh && fresh.status === 'disabled') {
          await logout();
          return false;
        }
        // 组织禁用兜底:所在单位（含个人体验空间）被禁用时强制登出
        const orgRes = await organizationApi.getCurrentOrganization();
        if (orgRes.data && orgRes.data.status === 'disabled') {
          await logout();
          return false;
        }
      } catch {
        // 状态查询失败不阻断登录流程,后续请求由后端统一拦截
      }
      return true;
    }

    // Mock 模式:沿用本地数据检查
    const statusCheck = getUserStatusCheck(user.id);
    if (statusCheck.disabled && statusCheck.reason && !statusCheck.reason.includes('不存在')) {
      await logout();
      return false;
    }

    // 修复单位审查#7：原「Mock 模式沿用 mock/organization.listOrganizations 查单位是否被禁用」
    // 整段删除——mock/organization.ts 已与真实链路脱节（管理端单位域全部直连后端），随之下线。
    // 因此 VITE_USE_MOCK=true 的本地演示模式不再有单位状态数据源，禁用单位的登出兜底只在
    // 后端模式生效，属预期结果（后端模式由上方 getCurrentOrganization 负责）。
    return true;
  }

  async function login(params: LoginParams): Promise<LoginResult> {
    state.isLoading = true;
    try {
      let result: LoginResult;

      if (USE_BACKEND_API) {
        // Use backend API
        result = await authService.login(params);
      } else {
        // Use mock data
        result = await mockLogin({
          method: params.method,
          mobile: 'mobile' in params ? params.mobile : undefined,
          code: 'code' in params ? params.code : undefined,
          username: 'username' in params ? params.username : undefined,
          password: 'password' in params ? params.password : undefined,
          authCode: 'authCode' in params ? params.authCode : undefined,
        });
      }

      if (result.success && result.user && result.token) {
        state.currentUser = result.user;
        state.token = result.token;
        state.isAuthenticated = true;
        // 修复登录审查#13：mock 模式不经 authService.login（后端模式由其调用 setTokens），
        // 此前仅 saveToStorage 写用户 JSON、token 从未落盘，刷新页面即掉线
        if (!USE_BACKEND_API) {
          setTokens(result.token, result.refreshToken ?? '', result.expiresIn);
        }
        saveToStorage();
        await checkOrgStatus();
      }

      return result;
    } finally {
      state.isLoading = false;
    }
  }

  async function logout(): Promise<void> {
    // 登出前清理工作台缓存(按当前用户键),防止同浏览器切换账号后读到上一账号数据。
    // 动态 import 避免与 workspace store 形成静态循环依赖。
    try {
      const { useWorkspaceStore } = await import('@/stores/workspace');
      useWorkspaceStore().clear();
    } catch {
      // store 未就绪等极端场景忽略
    }
    // 同口径清理智能分析离线缓存（analysis:cache:{userId}，防止跨账号残留）
    try {
      localStorage.removeItem(`analysis:cache:${state.currentUser?.id ?? 'anonymous'}`);
    } catch {
      // localStorage 不可用忽略
    }
    try {
      if (USE_BACKEND_API && state.token) {
        await authService.logout();
      } else if (state.token) {
        await mockLogout(state.token);
      }
    } catch (e) {
      console.warn('Logout API call failed, clearing local session:', e);
    }
    state.currentUser = null;
    state.token = null;
    state.isAuthenticated = false;
    clearTokens();
    saveToStorage();
  }

  function checkAuth(): boolean {
    if (!state.isAuthenticated) {
      loadFromStorage();
    }
    return state.isAuthenticated;
  }

  function updateCurrentUser(user: Partial<UserAccount>) {
    if (state.currentUser) {
      state.currentUser = { ...state.currentUser, ...user };
      saveToStorage();
    }
  }

  function isPersonalSpace(): boolean {
    return state.currentUser?.organizationType === 'personal';
  }

  async function refreshUser(): Promise<void> {
    if (!state.isAuthenticated || !USE_BACKEND_API) return;
    try {
      const freshUser = await authService.getCurrentUser();
      if (freshUser) {
        state.currentUser = freshUser;
        saveToStorage();
      }
    } catch {
      // 静默失败，不影响当前会话
    }
  }

  // 监听 session 过期事件，执行完整登出以同步 reactive auth state
  // 使用标志位防止 createAuth 被多次调用时重复注册监听器
  if (typeof window !== 'undefined' && !sessionExpiredListenerRegistered) {
    sessionExpiredListenerRegistered = true;
    window.addEventListener('auth:session-expired', () => {
      logout().catch(() => {
        // 兜底：即使 logout API 失败，也确保本地状态清理
        state.currentUser = null;
        state.token = null;
        state.isAuthenticated = false;
        clearTokens();
      });
    });
  }

  return {
    state: readonly(state) as Readonly<AuthState>,
    login,
    logout,
    checkAuth,
    updateCurrentUser,
    isPersonalSpace,
    refreshUser,
  };
}

export function provideAuth(app: App) {
  const auth = createAuth();
  app.provide(authKey, auth);
}

export function useAuth(): UseAuthReturn {
  const instance = getCurrentInstance()
  if (instance) {
    const auth = inject(authKey)
    if (auth) return auth
  }
  // Fallback for usage outside of setup context (e.g. router guards, Pinia stores)
  return createAuth()
}
